import { useTranslation } from "react-i18next";
import { CURRENCIES, formatPrice, type PriceMap } from "@nh/shared";

export interface PriceCellProps {
  prices: PriceMap;
  /** Lo que se pinta tachado al lado: precio de lista, precio anterior. */
  compareAtPrices?: PriceMap | undefined;
}

/**
 * El precio de una fila, en las dos monedas.
 *
 * Se enseñan las DOS y no "la primera que tenga valor": el panel existe
 * para detectar justo lo que esa optimización esconde —algo que se quedó
 * sin precio en dólares y que la tienda en inglés no puede vender—. Una
 * moneda sin precio se ve vacía a propósito.
 *
 * El formato sale de `formatPrice` del contrato, con el locale del
 * idioma activo. NADIE divide entre 100 aquí: los centavos entran tal
 * como los guardó el servidor y salen ya escritos.
 *
 * Es la versión genérica de `products/components/ProductPrices.tsx`
 * —misma lógica, sin saber de qué es el precio— porque un paquete
 * enseña exactamente lo mismo, con su precio de lista tachado al lado.
 */
export function PriceCell({ prices, compareAtPrices }: PriceCellProps) {
  const { i18n } = useTranslation();
  const locale = i18n.language.startsWith("en") ? "en-US" : "es-MX";

  const withPrice = CURRENCIES.filter((currency) => prices[currency] !== undefined);
  if (withPrice.length === 0) {
    return <span className="text-ds-ink-3">—</span>;
  }

  return (
    <div className="flex flex-col items-end gap-0.5">
      {withPrice.map((currency) => {
        const cents = prices[currency] as number;
        const compareAt = compareAtPrices?.[currency];
        return (
          <span key={currency} className="flex items-baseline justify-end gap-1.5">
            {compareAt !== undefined && compareAt > cents && (
              <s className="text-[11px] text-ds-ink-3">
                {formatPrice(compareAt, currency, locale)}
              </s>
            )}
            {/*
              No se añade la etiqueta "MXN"/"USD" al lado: `formatPrice`
              ya distingue las monedas según el idioma —en español, la
              local sale como "$1,299.50" y la extranjera como
              "USD 65.00"— y ponerla otra vez se leería "USD 65.00 USD".
            */}
            <span className="font-semibold tabular-nums text-ds-ink">
              {formatPrice(cents, currency, locale)}
            </span>
          </span>
        );
      })}
    </div>
  );
}
